import PropTypes from 'prop-types';
import React, { Component, createRef } from 'react';
import Button from 'react-bootstrap/lib/Button';
import CropperJS from 'react-cropper';
import Thumbnail from 'components/Thumbnails/Thumbnail';
import styles from './ImageCropper2.module.scss';
import 'cropperjs/dist/cropper.css';

let cropperInstanceId = 0;

class ImageCropper extends Component {
  static propTypes = {
    aspectRatio: PropTypes.number,
    existingImage: PropTypes.object,
    existingImageCircle: PropTypes.bool,
    /** Preload a File into the cropper (skips the upload step). */
    initialFile: PropTypes.object,
    /** Preload a data URL / image URL into the cropper. Prefer initialFile when sending the File to the API. */
    initialSrc: PropTypes.string,
    /** Optional crop box to restore (image natural-pixel coords from cropperjs). */
    initialCrop: PropTypes.shape({
      x: PropTypes.number,
      y: PropTypes.number,
      width: PropTypes.number,
      height: PropTypes.number,
    }),
    onDone: PropTypes.func,
    className: PropTypes.string,
    buttonAlign: PropTypes.oneOf(['center', 'right']),
  };

  static defaultProps = {
    aspectRatio: 1,
    existingImage: null,
    existingImageCircle: false,
    initialFile: null,
    initialSrc: null,
    initialCrop: null,
    onDone: (details) => console.log('ImageCropper', details),
    buttonAlign: 'center',
  };

  constructor(props) {
    super(props);

    cropperInstanceId += 1;
    this.inputId = `image-cropper-upload-button-${cropperInstanceId}`;
    this.appliedInitialCrop = false;

    this.state = {
      cropData: null,
      originalImage: null,
      src: null,
    };

    this.cropperRef = createRef();
    this.fileInputRef = createRef();
  }

  componentDidMount() {
    this.loadInitialImage(this.props);
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.initialFile !== this.props.initialFile ||
      prevProps.initialSrc !== this.props.initialSrc
    ) {
      this.appliedInitialCrop = false;
      this.loadInitialImage(this.props);
    }
  }

  loadInitialImage = (props) => {
    const { initialFile, initialSrc } = props;

    if (initialFile instanceof File || initialFile instanceof Blob) {
      const reader = new FileReader();

      reader.onload = () => {
        this.setState({
          src: reader.result,
          originalImage: initialFile,
        });
      };

      reader.readAsDataURL(initialFile);
      return;
    }

    if (initialSrc) {
      this.setState({
        src: initialSrc,
        // URL string is accepted by the project thumbnail API.
        originalImage: initialSrc,
      });
    }
  };

  handleReady = () => {
    const { initialCrop } = this.props;
    const cropper = this.cropperRef.current && this.cropperRef.current.cropper;

    if (!cropper || !initialCrop || this.appliedInitialCrop) {
      return;
    }

    const { x, y, width, height } = initialCrop;
    if (
      typeof x !== 'number' ||
      typeof y !== 'number' ||
      typeof width !== 'number' ||
      typeof height !== 'number'
    ) {
      return;
    }

    this.appliedInitialCrop = true;
    cropper.setData({
      x: x < 0 ? 0 : x,
      y: y < 0 ? 0 : y,
      width,
      height,
    });
  };

  handleChangeFile = (event) => {
    event.preventDefault();

    let files;

    if (event.dataTransfer) {
      files = event.dataTransfer.files;
    } else if (event.target) {
      files = event.target.files;
    }

    if (!files || !files[0]) {
      return;
    }

    this.appliedInitialCrop = false;

    const reader = new FileReader();

    reader.onload = () => {
      this.setState({
        src: reader.result,
        originalImage: files[0],
      });
    };

    reader.readAsDataURL(files[0]);
  };

  handleCrop = (event) => {
    const cropData = event.detail;

    this.setState({
      cropData,
    });
  };

  handleDone = () => {
    if (!this.state.cropData || !this.cropperRef.current) {
      return;
    }

    let croppedImage = null;
    try {
      croppedImage = this.cropperRef.current.cropper
        .getCroppedCanvas()
        .toDataURL();
    } catch (err) {
      // Cross-origin thumbnails may taint the canvas; API still gets URL + crop.
      croppedImage = null;
    }

    this.props.onDone({
      x: this.state.cropData.x < 0 ? 0 : this.state.cropData.x,
      y: this.state.cropData.y < 0 ? 0 : this.state.cropData.y,
      width: this.state.cropData.width,
      height: this.state.cropData.height,
      image: this.state.originalImage || this.props.initialSrc || null,
      croppedImage,
    });
  };

  handlePrimaryClick = () => {
    if (this.state.src) {
      this.handleDone();
      return;
    }

    if (this.fileInputRef.current) {
      this.fileInputRef.current.click();
    }
  };

  render() {
    const {
      aspectRatio,
      existingImage,
      existingImageCircle,
      className,
      onDone,
      buttonAlign,
      initialFile,
      initialSrc,
      initialCrop,
      ...rest
    } = this.props;
    const { src } = this.state;

    return (
      <div
        className={styles.container + (className ? ' ' + className : '')}
        {...rest}
      >
        <div
          className={styles.mainImage}
          style={{ paddingBottom: `${(1 / aspectRatio) * 100}%` }}
        >
          {!src && (
            <Thumbnail
              circle={existingImageCircle}
              className={styles.thumbnail}
              thumbnails={existingImage}
              lazyLoad={false}
            />
          )}
          {src && (
            <CropperJS
              aspectRatio={aspectRatio}
              checkCrossOrigin={false}
              crop={this.handleCrop}
              ready={this.handleReady}
              guides={false}
              ref={this.cropperRef}
              src={src}
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
              }} // Does not respect className
              viewMode={2}
            />
          )}
        </div>
        <input
          ref={this.fileInputRef}
          onChange={this.handleChangeFile}
          type="file"
          id={this.inputId}
          accept=".png,.jpeg,.jpg,"
        />
        <Button
          bsStyle="primary"
          className={`${styles.button} ${
            buttonAlign === 'right' ? styles.buttonRight : ''
          }`}
          onClick={this.handlePrimaryClick}
        >
          {src ? 'Save' : 'Upload image'}
        </Button>
      </div>
    );
  }
}

export default ImageCropper;
